import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Loader2, Trash2, Mail, Phone, MessageCircle, Check, Archive, RotateCcw, Inbox } from "lucide-react";
import { AdminHeader } from "../../components/AdminHeader";
import { adminApi, clearToken, getToken } from "../../lib/adminApi";

const FILTERS = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "archived", label: "Archived" },
  { key: "all", label: "All" },
];

const STATUS_STYLE = {
  new: "bg-gold/15 text-gold",
  contacted: "bg-emerald-100 text-emerald-700",
  archived: "bg-navy/10 text-navy/60",
};

const fmtDate = (d) => {
  try { return new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

export default function AdminLeads() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("new");

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.listLeads();
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); nav("/admin"); }
      else toast.error("Could not load leads");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!getToken()) { nav("/admin"); return; }
    load();
  }, [load, nav]);

  const setStatus = async (id, status) => {
    try {
      await adminApi.updateLead(id, status);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); nav("/admin"); }
      else toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await adminApi.deleteLead(id);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const counts = {
    new: leads.filter((l) => (l.status || "new") === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    archived: leads.filter((l) => l.status === "archived").length,
    all: leads.length,
  };

  const shown = filter === "all" ? leads : leads.filter((l) => (l.status || "new") === filter);
  const waLink = (phone) => `https://wa.me/${(phone || "").replace(/[^0-9]/g, "")}`;

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-leads">
      <AdminHeader />

      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-semibold text-navy">Leads</h1>
            <p className="mt-1 text-sm text-muted">Enquiries submitted through your website contact forms.</p>
          </div>
          <div className="flex flex-wrap gap-1 rounded-full bg-white p-1 shadow-soft" data-testid="lead-filters">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`lead-filter-${f.key}`}
                className={`rounded-full px-4 py-2 text-sm font-medium transition ${filter === f.key ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}>
                {f.label} <span className="opacity-70">({counts[f.key]})</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-gold" size={30} /></div>
        ) : shown.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-dashed border-navy/20 bg-white p-12 text-center" data-testid="leads-empty">
            <Inbox className="mx-auto text-navy/30" size={34} />
            <p className="mt-3 font-display text-xl font-semibold text-navy">No {filter !== "all" ? filter : ""} leads</p>
            <p className="mt-2 text-sm text-muted">New enquiries from your website will appear here.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {shown.map((l) => {
              const status = l.status || "new";
              return (
                <div key={l.id} className="rounded-2xl bg-white p-5 shadow-soft" data-testid={`lead-${l.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-display text-lg font-semibold text-navy">{l.name}</h3>
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[status] || STATUS_STYLE.new}`} data-testid={`lead-status-${l.id}`}>{status}</span>
                      </div>
                      <p className="mt-1 text-xs text-muted">{fmtDate(l.created_at)}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a href={`mailto:${l.email}`} data-testid={`lead-email-${l.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 px-3 py-1.5 text-xs font-medium text-navy transition hover:bg-navy hover:text-white"><Mail size={13} /> Email</a>
                      <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 px-3 py-1.5 text-xs font-medium text-navy transition hover:bg-navy hover:text-white"><Phone size={13} /> Call</a>
                      <a href={waLink(l.phone)} target="_blank" rel="noreferrer" data-testid={`lead-wa-${l.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-wa/10 px-3 py-1.5 text-xs font-medium text-wa transition hover:bg-wa hover:text-white"><MessageCircle size={13} /> WhatsApp</a>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
                    <p><span className="text-muted">Email: </span><a href={`mailto:${l.email}`} className="font-medium text-navy hover:text-gold">{l.email}</a></p>
                    <p><span className="text-muted">Phone: </span><span className="font-medium text-navy">{l.phone}</span></p>
                    <p><span className="text-muted">Budget: </span><span className="font-medium text-navy">{l.budget || "—"}</span></p>
                    <p><span className="text-muted">Interested in: </span><span className="font-medium text-navy">{l.interest || "General enquiry"}</span></p>
                  </div>

                  {l.message && (
                    <div className="mt-3 rounded-xl bg-sand/60 px-4 py-3 text-sm text-navy/80">{l.message}</div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2 border-t border-navy/10 pt-4">
                    {status !== "contacted" && (
                      <button onClick={() => setStatus(l.id, "contacted")} data-testid={`lead-contacted-${l.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-medium text-white transition hover:bg-emerald-700"><Check size={13} /> Mark contacted</button>
                    )}
                    {status !== "new" && (
                      <button onClick={() => setStatus(l.id, "new")} className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-medium text-navy transition hover:bg-navy hover:text-white"><RotateCcw size={13} /> Mark new</button>
                    )}
                    {status !== "archived" && (
                      <button onClick={() => setStatus(l.id, "archived")} data-testid={`lead-archive-${l.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-medium text-navy transition hover:bg-navy hover:text-white"><Archive size={13} /> Archive</button>
                    )}
                    <button onClick={() => remove(l.id)} data-testid={`lead-delete-${l.id}`} className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-medium text-red-500 transition hover:bg-red-500 hover:text-white"><Trash2 size={13} /> Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Toaster position="top-center" richColors />
    </div>
  );
}
